import { createFunctionLibrary } from '@wix/thunderbolt-becky-root'
import type { BeckyModel, FeaturesModelInstance, SiteAssetsModule } from '@wix/thunderbolt-becky-root'
import { resolveBeckyModel } from './helpers/resolveBeckyModel'
import { maybeAppendErrorPages } from '../errorPages/getPayloadWithErrorPage'
import byRefModule from '../byRef'
import { BeckyFetch } from './utils/beckyFetch'
import { getBeckyTopology } from './helpers/topologyBuilder'
import type { RendererType } from '@wix/thunderbolt-symbols'
import { typedModelFactory } from './helpers/typedModelFactory'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'
import { AsyncNodes, AsyncStores, getAsyncStore } from '@wix/thunderbolt-async-state-manager-store'
import { addInvalidation, fetchShapes } from '@wix/thunderbolt-fetch-shapes'
import { NicknameToCompIdMaps } from '@wix/thunderbolt-becky-types'

const featuresCarmiLoaders: Record<RendererType | 'viewer', FeaturesModelInstance> = {
	viewer: require('!carmi/loader?compiler=simple!../../../thunderbolt-becky-carmi-ds/src/carmi/features.carmi'),
	react: require('!carmi/loader?compiler=simple!../../../thunderbolt-becky-carmi-ds/src/carmi/features.react.carmi'),
	'react-native': require('!carmi/loader?compiler=simple!../../../thunderbolt-becky-carmi-ds/src/carmi/features.reactNative.carmi'),
}

export const featuresModelFactory = (rendererType: RendererType | 'viewer') => {
	const features = featuresCarmiLoaders[rendererType]
	return typedModelFactory<FeaturesModelInstance>(features)
}

const featuresModule: SiteAssetsModule<FeaturesModelInstance> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
		schemas,
	})
	const byRef = byRefModule(moduleContext)
	let nicknameToCompIdMaps: NicknameToCompIdMaps | undefined

	return {
		execute: async (payload) => {
			const beckyTopology = getBeckyTopology(topologyGetter, payload)

			return byRef
				.execute(maybeAppendErrorPages(payload))
				.then((payloadWithResolvedRefs) => {
					nicknameToCompIdMaps = payloadWithResolvedRefs.nicknameToCompIdMaps
					return resolveBeckyModel({
						payload: payloadWithResolvedRefs,
						beckyTopology,
						fetchFn,
						metricsReporter,
						logger,
					})
				})
				.then(async (beckyModel) => {
					const asyncNodes: AsyncNodes = [
						{
							addInvalidation,
							afterInvalidation: (shapes) => {
								return funcLib.runAsyncAndReport(() => {
									return fetchShapes(
										shapes,
										fetchFn,
										beckyTopology.environmentTopology.mediaRootUrl,
										funcLib.buildSvgUrl,
										metricsReporter
									)
								}, 'tb-features-fetch-shapes')
							},
							name: 'shapeStore',
						},
					]

					const { shapeStore } = (await funcLib.runAsyncAndReport(
						async () => getAsyncStore(asyncNodes, beckyModel, metricsReporter, schemas),
						'tb-features-async-stores'
					)) as Pick<AsyncStores, 'shapeStore'>

					const beckyModelWithShapes: BeckyModel = { ...beckyModel, shapeStore }

					const featuresFuncLib = {
						...funcLib,
						setFetchRequestsStore: () => metricsReporter.reportError,
					}
					return funcLib.runAndReport<FeaturesModelInstance>(() => {
						const beckyModelFactory = featuresModelFactory(payload.rendererType || 'viewer')
						const ret = beckyModelFactory(beckyModelWithShapes, featuresFuncLib)
						return ret
					}, 'tb-features-model-calculation')
				})
				.then(({ stateRefs, structure, props }) => {
					return {
						stateRefs,
						structure,
						props,
						...(nicknameToCompIdMaps && { nicknameToCompIdMaps }),
					}
				})
		},
	}
}

export default featuresModule
